import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { Lot } from "src/app/models/lot.model";
import moment from "moment";
import { Subscription } from "rxjs";
import { Receipt } from "src/app/models/receipt.model";
import { DATE_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { InventoryTaxSummaryEntry } from "src/app/models/inventorytax-summary-entry.model";
import { InventoryTaxSummary } from "src/app/models/inventorytax-summary.model";

@Component({
    selector: "inventorytax-panel",
    templateUrl: "./inventorytax-panel.component.html",
    styleUrls: ["./inventorytax-panel.component.scss"],
    standalone: false
})
export class InventoryTaxPanelComponent implements OnInit, OnDestroy {

    /** define if is loading */
    protected loading: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** */
    protected lots: Lot[] = []

    // The search form group
    protected form: FormGroup | null = null

    // A list of inventory tax summary entries based on the given filters.
    protected entries: [InventoryTaxSummaryEntry] | null = null

    // Inventory tax summary totals based on the given filters.
    protected summary: InventoryTaxSummary | null = null

    // Tax type groupings of the entries.
    protected grouped: { [key: string]: { price: number; tax: number; rate: number; count: number; } } = {}

    // A debounce timer for search events. Used to wait for
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for changes to the lot filter. */
    private subscriptions: Subscription[] = []

    /** If the report is filtered by lot this will have a positive value. */
    protected get lotId() {
        return parseInt(this.form.get('lotId').value)
    }

    /** A human readable date span based on the user selections. */
    protected get dateSpan() {
        const from = moment(this.form.get('from').value)
        const to = moment(this.form.get('to').value)

        const span = `${from.format('ddd MMM Do, YYYY')}`

        if (to.isSameOrBefore(from)) {
            return span
        }

        return `${span} - ${to.format('ddd MMM Do, YYYY')}`
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            from: new FormControl<string>(moment().startOf('month').subtract(1, 'months').format('YYYY-MM-DD'), []),
            to: new FormControl<string>(moment().startOf('month').subtract(1, 'days').format('YYYY-MM-DD'), [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.reset()
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('from').valueChanges.subscribe((from) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.reset()
                    this.fetch()
                }, DATE_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('to').valueChanges.subscribe((to) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.reset()
                    this.fetch()
                }, DATE_DEBOUNCE_MILLIS)
            })
        )

        this.reset()
        this.fetch()

        if (this.store && this.store.userAccount && this.store.userAccount['user'] &&
            this.store.userAccount['user'].lots) {
            this.lots = this.store.userAccount['user'].lots
        }
    }

    ngOnDestroy() {
        this.reset()

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.grouped = {}
        this.summary = null
        this.entries = null

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Get the summary and list of receivables needed for the report.
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'cash flow', 'view')) {
            return
        }

        if (this.loading) { // already getting the data
            return
        }

        let lotParam = ''
        const id = parseInt(this.form.get('lotId').value)
        let prefix = '?'
        if (id && (!isNaN(id) && (id > 0))) {
            lotParam = `?lot=${id}`
            prefix = '&'
        }

        let fromParam = ''
        const from = this.form.get('from').value
        if (from) {
            fromParam = `${prefix}from=${moment(from).startOf('day').format()}`
            prefix = '&'
        }

        let toParam = ''
        const to = this.form.get('to').value
        if (to) {
            toParam = `${prefix}to=${moment(to).endOf('day').format()}`
        }

        this.loading = true
        await this.master.getAsync(`reports/inventorytax${lotParam}${fromParam}${toParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.entries = res.data.summary
            this.constructSummary()
            this.constructGroups()
        })
    }

    /**
     * 
     */
    private constructSummary() {
        if (!this.entries || (this.entries.length <= 0)) {
            return
        }

        this.summary = new InventoryTaxSummary()
        this.summary.name = this.entries[0].name
        this.summary.address = this.entries[0].address
        this.summary.cityStateZip = this.entries[0].cityStateZip
        this.summary.dealerNumber = this.entries[0].dealerNumber
        this.summary.einNumber = this.entries[0].einNumber

        for (const entry of this.entries) {
            this.summary.count += 1
            this.summary.price += entry.price
            this.summary.tax += entry.tax || 0
        }
    }

    /**
     * Group the entries by tax type and calculate subtotals for each group.
     */
    private constructGroups() {
        if (!this.entries || (this.entries.length <= 0)) {
            return
        }

        for (const entry of this.entries) {
            const key: string = entry.taxType || ''
            if (!this.grouped[key]) {
                this.grouped[key] = {
                    price: 0,
                    tax: 0,
                    rate: 0,
                    count: 0
                }
            }

            this.grouped[key].price += entry.price
            this.grouped[key].tax += entry.tax
            this.grouped[key].rate += entry.taxRate // didvided by count later to get an average.
            this.grouped[key].count += 1
        }
    }

    /**
     * listen to output of navbar
    */
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.lots = this.store.userAccount['user'].lots
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
